import { useState } from 'react';
import { motion } from 'framer-motion';

export interface CreateRoomConfig {
  name: string;
  maxPlayers: number;
  gameSpeed: 'slow' | 'normal' | 'fast';
  callerMode: 'auto' | 'manual';
  prizePreset: 'standard' | 'advanced';
  allowLateMarking: boolean;
  callInterval?: number;
  disqualifyOnWrongClick?: boolean;
}

interface CreateRoomPanelProps {
  onCreateRoom: (roomConfig: CreateRoomConfig) => void;
}

export function CreateRoomPanel({ onCreateRoom }: CreateRoomPanelProps) {
  const [roomName, setRoomName] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(30);
  const [gameSpeed, setGameSpeed] = useState<'slow' | 'normal' | 'fast'>('normal');
  const [callerMode, setCallerMode] = useState<'auto' | 'manual'>('auto');
  const [prizePreset, setPrizePreset] = useState<'standard' | 'advanced'>('standard');
  const [allowLateMarking, setAllowLateMarking] = useState(true);
  const [callInterval, setCallInterval] = useState(5);
  const [disqualifyOnWrongClick, setDisqualifyOnWrongClick] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreateRoom({
      name: roomName || 'Quick Tambola Room',
      maxPlayers,
      gameSpeed: 'normal',
      callerMode,
      prizePreset: 'standard',
      allowLateMarking,
      callInterval,
      disqualifyOnWrongClick,
    });
  };

  return (
    <div className="glass rounded-3xl p-5 border border-white/10 shadow-glow-lg flex flex-col justify-between h-full text-left">
      <div>
        <h3 className="font-display text-base font-black tracking-wider text-amber-300 uppercase mb-4">
          Create New Room
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Room Name Input */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-surface-400">
              Room Name (Optional)
            </label>
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="Enter room name..."
              className="w-full px-3 py-2 text-xs rounded-xl bg-slate-950/40 border border-white/5 text-white placeholder-slate-500 font-medium focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/30 transition-all duration-300 shadow-inner"
            />
          </div>

          {/* Max Players selection slider */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-bold uppercase tracking-wider text-surface-400">
                Maximum Players
              </label>
              <span className="text-xs font-black font-mono text-purple-300">
                {maxPlayers} Players
              </span>
            </div>
            <div className="flex items-center space-x-3 bg-slate-950/20 px-3 py-2 rounded-xl border border-white/5">
              <span className="text-[10px] font-bold font-mono text-surface-500">1</span>
              <input
                type="range"
                min="1"
                max="100"
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(parseInt(e.target.value, 10))}
                className="w-full accent-purple-500 bg-slate-950/50 h-1.5 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-[10px] font-bold font-mono text-surface-500">100</span>
            </div>
          </div>

          {/* Call Interval selection slider */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-bold uppercase tracking-wider text-surface-400">
                Number Call Interval
              </label>
              <span className="text-xs font-black font-mono text-amber-300">
                {callInterval} Seconds
              </span>
            </div>
            <div className="flex items-center space-x-3 bg-slate-950/20 px-3 py-2 rounded-xl border border-white/5">
              <span className="text-[10px] font-bold font-mono text-surface-500">1s</span>
              <input
                type="range"
                min="1"
                max="10"
                value={callInterval}
                onChange={(e) => setCallInterval(parseInt(e.target.value, 10))}
                className="w-full accent-amber-500 bg-slate-950/50 h-1.5 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-[10px] font-bold font-mono text-surface-500">10s</span>
            </div>
          </div>

          {/* Caller Mode selector */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-surface-400">
              Caller Mode
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {(['auto', 'manual'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setCallerMode(mode)}
                  className={`py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer ${
                    callerMode === mode
                      ? 'bg-purple-600/30 border-purple-500 text-white shadow-glow'
                      : 'bg-slate-950/20 border-white/5 text-surface-400 hover:text-white hover:bg-slate-950/40'
                  }`}
                >
                  {mode === 'auto' ? 'Automatic' : 'Manual'}
                </button>
              ))}
            </div>
          </div>

          {/* Late Marking selector */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-surface-400">
              Late Marking (Missed Numbers)
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {([true, false] as const).map((value) => (
                <button
                  key={value ? 'allowed' : 'not-allowed'}
                  type="button"
                  onClick={() => setAllowLateMarking(value)}
                  className={`py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer ${
                    allowLateMarking === value
                      ? 'bg-purple-600/30 border-purple-500 text-white shadow-glow'
                      : 'bg-slate-950/20 border-white/5 text-surface-400 hover:text-white hover:bg-slate-950/40'
                  }`}
                >
                  {value ? 'Allowed' : 'Not Allowed'}
                </button>
              ))}
            </div>
            <p className="text-[8px] text-surface-500 font-semibold italic mt-1 leading-normal">
              {allowLateMarking 
                ? "Players can mark older announced numbers at any time."
                : "Strict rules! Players can only mark the current active number."}
            </p>
          </div>

          {/* Wrong Click Penalty selector */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-surface-400">
              Wrong Number Click Penalty
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {([true, false] as const).map((value) => (
                <button
                  key={value ? 'out' : 'no-penalty'}
                  type="button"
                  onClick={() => setDisqualifyOnWrongClick(value)}
                  className={`py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer ${
                    disqualifyOnWrongClick === value
                      ? 'bg-purple-600/30 border-purple-500 text-white shadow-glow'
                      : 'bg-slate-950/20 border-white/5 text-surface-400 hover:text-white hover:bg-slate-950/40'
                  }`}
                >
                  {value ? 'Disqualify Player' : 'No Penalty'}
                </button>
              ))}
            </div>
            <p className="text-[8px] text-surface-500 font-semibold italic mt-1 leading-normal">
              {disqualifyOnWrongClick 
                ? "Strict rules! Clicking a wrong/unannounced number will make you OUT."
                : "No penalty. Clicking wrong numbers has no effect on gameplay."}
            </p>
          </div>
        </form>
      </div>

      <div className="mt-6 pt-4 border-t border-white/5">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSubmit}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 via-accent-500 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-black tracking-widest uppercase text-xs border border-purple-400/20 transition-all duration-300 shadow-glow-accent cursor-pointer flex items-center justify-center"
        >
          Create Room
        </motion.button>
      </div>
    </div>
  );
}
